import Phaser from 'phaser';
import { ASSET_KEYS, COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, fontStr } from '@/config';
import { gameState } from '@/state/store';
import { STAT_LABEL } from '@/data/courseCatalog';
import type {
  AuthorStats,
  ContractedBook,
  SpillerForfatter,
  SvenManus,
  SvenManusPubliseringsvei,
} from '@/data/types';
import {
  SPILLER_FORFATTER_PUBLISERINGSKLAR_BIDRAG,
  SVEN_SPILLER_AUTHOR_ID,
  spillerFulltNavn,
} from '@/data/types';
import { finnSjanger } from '@/systems/svenManus';
import { animateModalGlide } from '@/ui/modalGlide';

const SHEET_WIDTH = 880;
const PADDING = 28;

const STAT_KEYS: ReadonlyArray<keyof AuthorStats> = [
  'dialog',
  'plot',
  'karakter',
  'spraak',
  'voice',
  'autentisitet',
  'disiplin',
];

export type MinKarriereCallbacks = {
  onStartManus?: () => void;
  onSkrivIDag?: () => void;
  onVelgPubliseringsvei?: () => void;
};

export class MinKarriereModal {
  private layer?: Phaser.GameObjects.Container;
  private unsubscribe?: () => void;
  private callbacks: MinKarriereCallbacks = {};

  constructor(private scene: Phaser.Scene) {}

  get isOpen(): boolean {
    return !!this.layer;
  }

  setCallbacks(cb: MinKarriereCallbacks): void {
    this.callbacks = cb;
  }

  open(): void {
    if (this.layer) return;
    this.layer = this.scene.add.container(0, 0).setDepth(160);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.72)
      .setOrigin(0, 0)
      .setInteractive();
    backdrop.on('pointerup', () => this.close());
    this.layer.add(backdrop);

    this.render();
    animateModalGlide(this.scene, this.layer);
    this.unsubscribe = gameState.subscribe(() => this.render());
  }

  close(): void {
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.layer?.destroy(true);
    this.layer = undefined;
  }

  private render(): void {
    if (!this.layer) return;
    while (this.layer.list.length > 1) {
      const last = this.layer.list[this.layer.list.length - 1];
      this.layer.remove(last, true);
    }

    const sf = gameState.spillerForfatter;
    const sheetX = (GAME_WIDTH - SHEET_WIDTH) / 2;
    const sheetY = 60;
    const sheetH = GAME_HEIGHT - 120;

    const sheet = this.scene.add
      .rectangle(sheetX, sheetY, SHEET_WIDTH, sheetH, COLORS.beige, 0.98)
      .setOrigin(0, 0)
      .setStrokeStyle(3, COLORS.wine, 0.7);
    this.layer.add(sheet);

    const topBorder = this.scene.add
      .rectangle(sheetX, sheetY, SHEET_WIDTH, 4, COLORS.wine, 1)
      .setOrigin(0, 0);
    this.layer.add(topBorder);

    this.byggHeader(sheetX, sheetY);
    this.byggPortrettOgInfo(sheetX, sheetY + 120, sf);
    let cursorY = sheetY + 260;
    cursorY = this.byggStats(sheetX, cursorY, sf.stats);
    cursorY += 16;
    cursorY = this.byggSkriveModul(sheetX, cursorY, sf);
    cursorY += 16;
    cursorY = this.byggBidragsTeller(sheetX, cursorY, sf);
    cursorY += 16;
    cursorY = this.byggMineBoker(sheetX, cursorY, sf);
    cursorY += 16;
    this.byggPubliseringsklarTekst(sheetX, cursorY, sf);
  }

  private byggHeader(sheetX: number, sheetY: number): void {
    if (!this.layer) return;
    const tittel = this.scene.add
      .text(sheetX + PADDING, sheetY + 28, 'Min karriere', {
        fontFamily: FONTS.display,
        fontSize: fontStr(46),
        color: '#862736',
        wordWrap: { width: SHEET_WIDTH - PADDING * 2 - 100 },
      })
      .setOrigin(0, 0);
    this.layer.add(tittel);

    const sfNavn = gameState.spillerForfatter.navn;
    const undertittel = this.scene.add
      .text(
        sheetX + PADDING,
        sheetY + 108,
        `${spillerFulltNavn(sfNavn)} — aspirerende forfatter ved Forfatterskolen`,
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(18),
          color: '#5a4a3a',
          fontStyle: 'italic',
          wordWrap: { width: SHEET_WIDTH - PADDING * 2 - 100 },
        },
      )
      .setOrigin(0, 0);
    this.layer.add(undertittel);

    const closeBtn = this.scene.add
      .text(sheetX + SHEET_WIDTH - PADDING, sheetY + 4, '×', {
        fontFamily: FONTS.display,
        fontSize: fontStr(64),
        color: '#1a1a1a',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    closeBtn.on('pointerup', () => this.close());
    this.layer.add(closeBtn);

    const endreNavn = this.scene.add
      .text(sheetX + PADDING, sheetY + 148, '✎  Endre navn', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(16),
        color: '#862736',
        fontStyle: 'italic',
      })
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });
    endreNavn.on('pointerup', () => this.endreNavnPrompt());
    this.layer.add(endreNavn);
  }

  private endreNavnPrompt(): void {
    if (typeof window === 'undefined') return;
    const noaa = gameState.spillerForfatter.navn || 'Sven';
    const nytt = window.prompt('Hva heter du? (Brukes overalt i spillet)', noaa);
    if (nytt === null) return;
    const trimmet = nytt.trim();
    if (trimmet.length < 1) return;
    gameState.settSpillerNavn(trimmet.slice(0, 30));
    this.close();
    this.open();
  }

  private byggPortrettOgInfo(
    sheetX: number,
    y: number,
    sf: SpillerForfatter,
  ): void {
    if (!this.layer) return;
    const portrettX = sheetX + PADDING + 48;
    const portrettStr = 96;
    const cx = portrettX;
    const cy = y + portrettStr / 2;

    if (this.scene.textures.exists(ASSET_KEYS.playerSven)) {
      const bilde = this.scene.add
        .image(cx, cy, ASSET_KEYS.playerSven)
        .setDisplaySize(portrettStr, portrettStr);
      const maskGfx = this.scene.make.graphics({});
      maskGfx.fillStyle(0xffffff);
      maskGfx.fillCircle(cx, cy, portrettStr / 2);
      bilde.setMask(maskGfx.createGeometryMask());
      this.layer.add(bilde);
    } else {
      const fornavn = (sf.navn.trim() || 'Sven').trim();
      const initialBokstav = fornavn.charAt(0).toUpperCase() || 'S';
      const sirkel = this.scene.add
        .circle(cx, cy, portrettStr / 2, COLORS.ochre, 1)
        .setStrokeStyle(3, COLORS.wine, 1);
      const initial = this.scene.add
        .text(cx, cy, initialBokstav, {
          fontFamily: FONTS.display,
          fontSize: fontStr(48),
          color: '#1a1a1a',
          fontStyle: '700',
        })
        .setOrigin(0.5);
      this.layer.add([sirkel, initial]);
    }

    const ramme = this.scene.add
      .circle(cx, cy, portrettStr / 2 + 2)
      .setStrokeStyle(3, COLORS.wine, 1)
      .setFillStyle();
    this.layer.add(ramme);

    const tekstX = portrettX + portrettStr / 2 + 32;
    const snitt = beregnSnitt(sf.stats);
    const fornavn = (sf.navn.trim() || 'Sven').split(' ')[0];
    const navn = this.scene.add
      .text(tekstX, y + 8, `${fornavn} – aspirerende forfatter`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(26),
        color: '#1a1a1a',
      })
      .setOrigin(0, 0);
    this.layer.add(navn);

    const snittTxt = this.scene.add
      .text(
        tekstX,
        y + 46,
        `Stat-snitt: ${snitt.toFixed(1)} · Skrivelab-bidrag: ${sf.skrivelabBidrag}`,
        {
          fontFamily: FONTS.hud,
          fontSize: fontStr(18),
          color: '#5a4a3a',
          fontStyle: '600',
        },
      )
      .setOrigin(0, 0);
    this.layer.add(snittTxt);

    const desc = this.scene.add
      .text(
        tekstX,
        y + 76,
        sf.publiseringsklarErklart
          ? 'Du har bygd en stabil håndverkskunnskap.'
          : 'Hver Skrivelab-innsending bygger karrieren din.',
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(15),
          color: '#7a7060',
          fontStyle: 'italic',
          wordWrap: { width: SHEET_WIDTH - (tekstX - sheetX) - PADDING },
        },
      )
      .setOrigin(0, 0);
    this.layer.add(desc);
  }

  private byggStats(sheetX: number, y: number, stats: AuthorStats): number {
    if (!this.layer) return y;
    const innhX = sheetX + PADDING;
    const innhBredde = SHEET_WIDTH - PADDING * 2;

    const overskrift = this.scene.add
      .text(innhX, y, 'Forfatter-stats', {
        fontFamily: FONTS.display,
        fontSize: fontStr(24),
        color: '#1a1a1a',
      })
      .setOrigin(0, 0);
    this.layer.add(overskrift);

    const startY = y + 52;
    const radH = 40;
    for (let i = 0; i < STAT_KEYS.length; i += 1) {
      const key = STAT_KEYS[i];
      const radY = startY + i * radH;
      this.byggStatRad(innhX, radY, innhBredde, key, stats[key]);
    }
    return startY + STAT_KEYS.length * radH;
  }

  private byggStatRad(
    x: number,
    y: number,
    bredde: number,
    key: keyof AuthorStats,
    verdi: number,
  ): void {
    if (!this.layer) return;
    const labelW = 180;
    const valueW = 60;
    const barX = x + labelW;
    const barW = bredde - labelW - valueW - 12;
    const barH = 12;

    const label = this.scene.add
      .text(x, y, STAT_LABEL[key], {
        fontFamily: FONTS.body,
        fontSize: fontStr(17),
        color: '#1a1a1a',
        fontStyle: '600',
      })
      .setOrigin(0, 0);
    this.layer.add(label);

    const bg = this.scene.add
      .rectangle(barX, y + 8, barW, barH, COLORS.beigeDark, 1)
      .setOrigin(0, 0);
    this.layer.add(bg);

    const fyllBredde = Math.max(2, Math.round((barW * verdi) / 100));
    const fyllFarge = verdi >= 60 ? COLORS.forest : verdi >= 40 ? COLORS.ochre : COLORS.wine;
    const fyll = this.scene.add
      .rectangle(barX, y + 8, fyllBredde, barH, fyllFarge, 1)
      .setOrigin(0, 0);
    this.layer.add(fyll);

    const verdiTxt = this.scene.add
      .text(x + bredde, y + 4, String(verdi), {
        fontFamily: FONTS.hud,
        fontSize: fontStr(17),
        color: '#1a1a1a',
        fontStyle: '700',
      })
      .setOrigin(1, 0);
    this.layer.add(verdiTxt);
  }

  private byggSkriveModul(
    sheetX: number,
    y: number,
    sf: SpillerForfatter,
  ): number {
    if (!this.layer) return y;
    if (!sf.publiseringsklarErklart) return y;

    const innhX = sheetX + PADDING;
    const innhBredde = SHEET_WIDTH - PADDING * 2;

    if (!sf.aktivtManus) {
      return this.byggStartManusKnapp(innhX, y, innhBredde);
    }
    if (sf.aktivtManus.status === 'skriver') {
      return this.byggSkriverNaa(innhX, y, innhBredde, sf.aktivtManus, sf);
    }
    if (sf.aktivtManus.status === 'ferdig') {
      return this.byggFerdigManus(innhX, y, innhBredde, sf.aktivtManus);
    }
    return y;
  }

  private byggStartManusKnapp(x: number, y: number, w: number): number {
    if (!this.layer) return y;
    const kortH = 110;
    const bg = this.scene.add
      .rectangle(x, y, w, kortH, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.wine, 0.5);
    this.layer.add(bg);

    const venstre = this.scene.add
      .rectangle(x, y, 4, kortH, COLORS.wine, 1)
      .setOrigin(0, 0);
    this.layer.add(venstre);

    const tittel = this.scene.add
      .text(x + 18, y + 14, 'Skriv min egen bok', {
        fontFamily: FONTS.display,
        fontSize: fontStr(22),
        color: '#862736',
        wordWrap: { width: w - 260 },
      })
      .setOrigin(0, 0);
    this.layer.add(tittel);

    const hint = this.scene.add
      .text(x + 18, y + 60, 'Du er publiseringsklar. Velg en sjanger og start manuset.', {
        fontFamily: FONTS.body,
        fontSize: fontStr(14),
        color: '#5a4a3a',
        fontStyle: 'italic',
        wordWrap: { width: w - 260 },
      })
      .setOrigin(0, 0);
    this.layer.add(hint);

    const knappW = 220;
    const knappH = 60;
    const knappX = x + w - knappW - 16;
    const knappY = y + (kortH - knappH) / 2;
    const knappBg = this.scene.add
      .rectangle(knappX, knappY, knappW, knappH, COLORS.ochre, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.wine, 0.7);
    const knappTxt = this.scene.add
      .text(knappX + knappW / 2, knappY + knappH / 2, 'Velg sjanger →', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(18),
        color: '#1a1a1a',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    this.layer.add([knappBg, knappTxt]);
    knappBg.setInteractive({ useHandCursor: true });
    knappBg.on('pointerover', () => knappBg.setAlpha(0.85));
    knappBg.on('pointerout', () => knappBg.setAlpha(1));
    knappBg.on('pointerup', () => this.callbacks.onStartManus?.());
    return y + kortH;
  }

  private byggSkriverNaa(
    x: number,
    y: number,
    w: number,
    m: SvenManus,
    sf: SpillerForfatter,
  ): number {
    if (!this.layer) return y;
    const kortH = 170;
    const bg = this.scene.add
      .rectangle(x, y, w, kortH, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.wine, 0.5);
    this.layer.add(bg);

    const venstre = this.scene.add
      .rectangle(x, y, 4, kortH, COLORS.wine, 1)
      .setOrigin(0, 0);
    this.layer.add(venstre);

    const sjanger = finnSjanger(m.sjanger);
    const tittelTxt = this.scene.add
      .text(x + 18, y + 14, `«${m.tittel}»`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(22),
        color: '#862736',
        wordWrap: { width: w - 36 },
      })
      .setOrigin(0, 0);
    this.layer.add(tittelTxt);

    const sjangerTxt = this.scene.add
      .text(x + 18, y + 58, `${sjanger?.navn ?? m.sjanger} · ${m.daglig.toFixed(1)} progresjon/dag`, {
        fontFamily: FONTS.body,
        fontSize: fontStr(15),
        color: '#5a4a3a',
        fontStyle: 'italic',
        wordWrap: { width: w - 36 },
      })
      .setOrigin(0, 0);
    this.layer.add(sjangerTxt);

    const barX = x + 18;
    const barW = w - 36;
    const barY = y + 98;
    const barH = 16;
    const barBg = this.scene.add
      .rectangle(barX, barY, barW, barH, COLORS.beigeDark, 1)
      .setOrigin(0, 0);
    this.layer.add(barBg);
    const fyllW = Math.max(2, Math.round((barW * m.progresjon) / 100));
    const fyll = this.scene.add
      .rectangle(barX, barY, fyllW, barH, COLORS.wine, 1)
      .setOrigin(0, 0);
    this.layer.add(fyll);
    const pct = this.scene.add
      .text(barX + barW, barY - 2, `${m.progresjon.toFixed(0)}%`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: '#1a1a1a',
        fontStyle: '700',
      })
      .setOrigin(1, 1);
    this.layer.add(pct);

    const alleredeSkrevet =
      sf.sisteSkrivDag !== null && sf.sisteSkrivDag === gameState.dag;
    const knappW = 220;
    const knappH = 56;
    const knappX = x + w - knappW - 16;
    const knappY = y + kortH - knappH - 14;
    const knappBg = this.scene.add
      .rectangle(
        knappX,
        knappY,
        knappW,
        knappH,
        alleredeSkrevet ? COLORS.beigeDark : COLORS.forest,
        1,
      )
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.wine, 0.7);
    const knappTxt = this.scene.add
      .text(
        knappX + knappW / 2,
        knappY + knappH / 2,
        alleredeSkrevet ? 'Skrevet i dag ✓' : 'Skriv i dag',
        {
          fontFamily: FONTS.hud,
          fontSize: fontStr(16),
          color: alleredeSkrevet ? '#5a4a3a' : '#ffffff',
          fontStyle: '700',
        },
      )
      .setOrigin(0.5);
    this.layer.add([knappBg, knappTxt]);
    if (!alleredeSkrevet) {
      knappBg.setInteractive({ useHandCursor: true });
      knappBg.on('pointerover', () => knappBg.setAlpha(0.85));
      knappBg.on('pointerout', () => knappBg.setAlpha(1));
      knappBg.on('pointerup', () => this.callbacks.onSkrivIDag?.());
    }
    return y + kortH;
  }

  private byggFerdigManus(
    x: number,
    y: number,
    w: number,
    m: SvenManus,
  ): number {
    if (!this.layer) return y;
    const kortH = 140;
    const bg = this.scene.add
      .rectangle(x, y, w, kortH, COLORS.yellowWarm, 0.45)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.wine, 0.7);
    this.layer.add(bg);

    const venstre = this.scene.add
      .rectangle(x, y, 6, kortH, COLORS.wine, 1)
      .setOrigin(0, 0);
    this.layer.add(venstre);

    const tittel = this.scene.add
      .text(x + 18, y + 14, `✓ «${m.tittel}» er ferdig`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(22),
        color: '#862736',
        wordWrap: { width: w - 36 - 260 },
      })
      .setOrigin(0, 0);
    this.layer.add(tittel);

    const hint = this.scene.add
      .text(x + 18, y + 60, 'Velg publiseringsvei: eget forlag, Kapelle, Asketank eller Calidris.', {
        fontFamily: FONTS.body,
        fontSize: fontStr(14),
        color: '#5a4a3a',
        fontStyle: 'italic',
        wordWrap: { width: w - 36 - 260 },
      })
      .setOrigin(0, 0);
    this.layer.add(hint);

    const knappW = 240;
    const knappH = 60;
    const knappX = x + w - knappW - 16;
    const knappY = y + (kortH - knappH) / 2;
    const knappBg = this.scene.add
      .rectangle(knappX, knappY, knappW, knappH, COLORS.ochre, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.wine, 0.7);
    const knappTxt = this.scene.add
      .text(knappX + knappW / 2, knappY + knappH / 2, 'Velg publiseringsvei →', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(16),
        color: '#1a1a1a',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    this.layer.add([knappBg, knappTxt]);
    knappBg.setInteractive({ useHandCursor: true });
    knappBg.on('pointerover', () => knappBg.setAlpha(0.85));
    knappBg.on('pointerout', () => knappBg.setAlpha(1));
    knappBg.on('pointerup', () => this.callbacks.onVelgPubliseringsvei?.());
    return y + kortH;
  }

  private byggBidragsTeller(
    sheetX: number,
    y: number,
    sf: SpillerForfatter,
  ): number {
    if (!this.layer) return y;
    const innhX = sheetX + PADDING;
    const bredde = SHEET_WIDTH - PADDING * 2;
    const maal = SPILLER_FORFATTER_PUBLISERINGSKLAR_BIDRAG;
    const naa = Math.min(sf.skrivelabBidrag, maal);

    const tekst = this.scene.add
      .text(
        innhX,
        y,
        `Skrivelab-bidrag: ${sf.skrivelabBidrag} / ${maal}`,
        {
          fontFamily: FONTS.hud,
          fontSize: fontStr(16),
          color: '#1a1a1a',
          fontStyle: '700',
        },
      )
      .setOrigin(0, 0);
    this.layer.add(tekst);

    const barY = y + 26;
    const barH = 12;
    const bg = this.scene.add
      .rectangle(innhX, barY, bredde, barH, COLORS.beigeDark, 1)
      .setOrigin(0, 0);
    this.layer.add(bg);

    const fyllBredde = Math.max(2, Math.round((bredde * naa) / maal));
    const fyll = this.scene.add
      .rectangle(innhX, barY, fyllBredde, barH, COLORS.ochre, 1)
      .setOrigin(0, 0);
    this.layer.add(fyll);
    return barY + barH;
  }

  private byggMineBoker(
    sheetX: number,
    y: number,
    sf: SpillerForfatter,
  ): number {
    if (!this.layer) return y;
    const mine = this.hentMineBoker();
    if (mine.length === 0 && sf.manuskriptHistorikk.length === 0) return y;

    const innhX = sheetX + PADDING;
    const innhBredde = SHEET_WIDTH - PADDING * 2;

    const overskrift = this.scene.add
      .text(innhX, y, `Mine bøker (${mine.length})`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(22),
        color: '#1a1a1a',
      })
      .setOrigin(0, 0);
    this.layer.add(overskrift);

    const prisInfoTxt =
      sf.brageNominasjoner > 0 || sf.brageVinster > 0
        ? `Brage: ${sf.brageVinster} vinst${sf.brageVinster === 1 ? '' : 'er'} · ${sf.brageNominasjoner} nominasjon${sf.brageNominasjoner === 1 ? '' : 'er'}`
        : '';
    if (prisInfoTxt) {
      const t = this.scene.add
        .text(innhX + innhBredde, y + 4, prisInfoTxt, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(14),
          color: '#862736',
          fontStyle: '700',
        })
        .setOrigin(1, 0);
      this.layer.add(t);
    }

    let cursor = y + 48;
    const radH = 80;
    for (const bok of mine) {
      this.byggMinBokRad(innhX, cursor, innhBredde, bok, sf);
      cursor += radH + 8;
    }
    return cursor;
  }

  private hentMineBoker(): ContractedBook[] {
    const ut: ContractedBook[] = [];
    for (const b of gameState.iProduksjon) {
      if (b.authorId === SVEN_SPILLER_AUTHOR_ID && b.status === 'utgitt') {
        ut.push(b);
      }
    }
    for (const b of gameState.utgitteBoker) {
      if (b.authorId === SVEN_SPILLER_AUTHOR_ID) ut.push(b);
    }
    return ut;
  }

  private byggMinBokRad(
    x: number,
    y: number,
    w: number,
    bok: ContractedBook,
    sf: SpillerForfatter,
  ): void {
    if (!this.layer) return;
    const radH = 80;
    const bg = this.scene.add
      .rectangle(x, y, w, radH, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.beigeDark, 0.8);
    const venstre = this.scene.add
      .rectangle(x, y, 4, radH, COLORS.yellowWarm, 1)
      .setOrigin(0, 0);
    this.layer.add([bg, venstre]);

    const sjanger = finnSjanger(bok.genre);
    const tittelTxt = this.scene.add
      .text(x + 18, y + 10, `«${bok.title}»`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(18),
        color: '#1a1a1a',
        wordWrap: { width: w - 36 },
      })
      .setOrigin(0, 0);
    this.layer.add(tittelTxt);

    const vei = this.finnVei(bok.isbn, sf);
    const veiTxt = vei ? veiEtikett(vei) : 'ukjent vei';
    const meta = this.scene.add
      .text(
        x + 18,
        y + 48,
        `${sjanger?.navn ?? bok.genre} · ${veiTxt} · ${bok.salg.toLocaleString('nb-NO')} solgt`,
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(13),
          color: '#5a4a3a',
          fontStyle: 'italic',
        },
      )
      .setOrigin(0, 0);
    this.layer.add(meta);

    const snitt = anmeldelsesSnitt(bok);
    const snittStr = snitt > 0 ? `★ ${snitt.toFixed(1)}/6` : 'ingen anmeldelser ennå';
    const snittTxt = this.scene.add
      .text(x + w - 18, y + 10, snittStr, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(15),
        color: '#862736',
        fontStyle: '700',
      })
      .setOrigin(1, 0);
    this.layer.add(snittTxt);

    const brageStatus = this.brageStatusFor(bok);
    if (brageStatus) {
      const b = this.scene.add
        .text(x + w - 18, y + 34, brageStatus, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(12),
          color: '#3d5a4d',
          fontStyle: '700',
        })
        .setOrigin(1, 0);
      this.layer.add(b);
    }
  }

  private finnVei(
    isbn: string,
    sf: SpillerForfatter,
  ): SvenManusPubliseringsvei | undefined {
    for (const m of sf.manuskriptHistorikk) {
      if (m.utgittSomISBN === isbn) return m.publiseringsvei;
    }
    if (sf.aktivtManus?.utgittSomISBN === isbn) {
      return sf.aktivtManus.publiseringsvei;
    }
    return undefined;
  }

  private brageStatusFor(bok: ContractedBook): string | null {
    const noms = gameState.brageNominasjoner;
    let nominert = false;
    for (const n of noms) {
      if (n.bokIsbn === bok.isbn) {
        nominert = true;
        break;
      }
    }
    const hist = gameState.brageHistorikk;
    for (const v of hist) {
      if (v.bokIsbn === bok.isbn) return '🏆 Brage-vinner';
    }
    return nominert ? '✦ Brage-nominert' : null;
  }

  private byggPubliseringsklarTekst(
    sheetX: number,
    y: number,
    sf: SpillerForfatter,
  ): void {
    if (!this.layer) return;
    const innhX = sheetX + PADDING;
    const bredde = SHEET_WIDTH - PADDING * 2;

    if (sf.publiseringsklarErklart) {
      const status = this.scene.add
        .text(
          innhX,
          y,
          sf.manuskriptHistorikk.length > 0
            ? `Du har ${sf.manuskriptHistorikk.length} fullført ${sf.manuskriptHistorikk.length === 1 ? 'manus' : 'manuskripter'}.`
            : '✓ Publiseringsklar — start ditt første manus over.',
          {
            fontFamily: FONTS.body,
            fontSize: fontStr(15),
            color: '#3d5a4d',
            fontStyle: '600',
          },
        )
        .setOrigin(0, 0);
      this.layer.add(status);
      return;
    }

    const snitt = beregnSnitt(sf.stats);
    const trengerSnitt = Math.max(0, 60 - snitt);
    const trengerBidrag = Math.max(
      0,
      SPILLER_FORFATTER_PUBLISERINGSKLAR_BIDRAG - sf.skrivelabBidrag,
    );

    const status = this.scene.add
      .text(
        innhX,
        y,
        trengerBidrag === 0 && trengerSnitt === 0
          ? 'Du er klar.'
          : `Mangler: ${
              trengerBidrag > 0 ? `${trengerBidrag} bidrag` : ''
            }${trengerBidrag > 0 && trengerSnitt > 0 ? ' og ' : ''}${
              trengerSnitt > 0 ? `${trengerSnitt.toFixed(1)} i stat-snitt` : ''
            } før du er publiseringsklar.`,
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(15),
          color: '#7a7060',
          fontStyle: 'italic',
          wordWrap: { width: bredde },
        },
      )
      .setOrigin(0, 0);
    this.layer.add(status);
  }
}

function beregnSnitt(stats: AuthorStats): number {
  let sum = 0;
  for (const k of STAT_KEYS) sum += stats[k];
  return sum / STAT_KEYS.length;
}

function anmeldelsesSnitt(bok: ContractedBook): number {
  if (bok.anmeldelser.length === 0) return 0;
  let sum = 0;
  for (const a of bok.anmeldelser) sum += a.terninger;
  return sum / bok.anmeldelser.length;
}

const VEI_ETIKETT: Record<SvenManusPubliseringsvei, string> = {
  'eget-forlag': 'eget forlag',
  kapelle: 'Kapelle Damm',
  asketank: 'Asketank',
  gullsand: 'Gullsand',
  calidris: 'Calidris',
};

function veiEtikett(v: SvenManusPubliseringsvei): string {
  return VEI_ETIKETT[v];
}
